import type {
  BatchAssignment,
  ModuleSnapshot,
  RecipeLabwareDef,
  SampleGroup,
  TrayPath,
  WasteContainer,
} from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { TC_STOCK_SLOTS } from '../../figures/deckRackDefinitions.ts'
import { labwareOf } from '../recipes/recipeV2View.ts'
import { trayPathOf } from '../recipes/trayPath.ts'

import type { QuickResetStep } from './devQuickReset.ts'
import { assignmentsBody } from './newBatchAssignments.ts'

/**
 * 개발용 「테스트 작업 시작」의 **순수 모델** — 무엇을 만들고 어디까지 왔는지.
 *
 * 🔴 **서버에 새 창구를 내지 않는다.** 관리자 대시보드의 「테스트 배치」
 * (`../ctcells-core/app/resources/admin/pages/test_batch.js` — 권위다)와 **같은 순서로**
 * 계약 창구 넷을 엮는다:
 * ```
 * POST /control-token  (없을 때만)
 * POST /modules/{id}/consumables/replenishment  (모자란 것만 · 아래 「소모품 자동 보충」)
 * POST /samples  × 5   → POST /batches → POST /batches/{id}/preflight → POST /batches/{id}/start
 * ```
 * 권한을 하나도 넓히지 않는다 — 인증·역할·제어락·감사가 전부 그대로 지난다.
 *
 * 🔴 **시작은 `useStartGate` 하나를 쓴다**(작업 줄의 [시작]과 같은 것). 여기에 시작 경로를
 *    갈라 지으면 한쪽만 경고 확약을 싣게 되고, 그 순간 서버가 409 로 거절하거나
 *    **무엇을 알고 시작했는지가 원장에서 빈다**.
 */

/**
 * 🔴 **시험 회차는 「환자 4명 × 챔버 2개 + Reference 1 = 시료 9건」으로 고정한다**(eric 2026-10-07 「버튼 시료 구성
 *    환자 4명 챔버 2개 대조 1건 아니야? 테스트 시작은 항상 그렇게 해줘」 · 종전 2026-09-22 「환자 4 + 대조 1 = 5」를 바꿨다).
 *
 * 🔴 왜 챔버마다 시료 한 건인가 — v0.9.3 서버는 **시료 1건 = 챔버 1개**로 보고, 같은 환자 번호의 챔버 수가 짝수(2·4)여야
 *    원심 짝을 짓는다(`recipe_v2.batch_doc.derive` · 어기면 Pre-flight `BATCH_SAMPLE_SET_UNSUPPORTED` `patient_odd`).
 *    종전 5건(환자마다 챔버 1)은 그 점검에서 막혔다(작업 `53916c3b` · 2026-10-07).
 *    ⇒ 환자마다 **같은 환자 번호**로 시료 `QUICK_CHAMBERS_PER_PATIENT` 건을 만들어 그 환자 묶음의 웰에 하나씩 앉힌다.
 *
 * ⚠ 정본(`01_제품정의`)과 core 관리자 페이지(`app/resources/admin/pages/test_batch.js`
 *    의 `TEST_SAMPLES = 8`)는 「1 배치 = 시료 8 + Reference 1」이다. **그 값을 부정하는 것이
 *    아니라 시험 편의로 낮춘 것**이고, 이 창구에만 적용된다.
 *
 * 🔴 **Reference 는 남긴다.** 이 회차가 쓰는 레시피 `fe-20260922-full-r1` 의
 *    `payload.tray_path.sample_groups` 가 실물에서 `P1`~`P4`(role `patient`) + `REF`
 *    (role `reference`) **다섯**이다(2026-09-22 `GET /recipes/{id}` 로 확인). 4+1 이 그 꼴이다.
 *
 * 🔴 **수를 바꿀 일이 생기면 이 상수 하나만 바꾼다.** 아래 어디에도 숫자를 다시 적지 않는다.
 */
/** 환자 수 */
export const QUICK_SAMPLE_COUNT = 4
/** 환자 한 명의 챔버 수 — 시료 한 건이 챔버 하나다 */
export const QUICK_CHAMBERS_PER_PATIENT = 2
export const QUICK_REFERENCE_COUNT = 1
export const QUICK_TOTAL_SAMPLES =
  QUICK_SAMPLE_COUNT * QUICK_CHAMBERS_PER_PATIENT + QUICK_REFERENCE_COUNT

/**
 * 🔴 **시험 행임을 «메타데이터»에 남긴다.** 접두(`TEST-…`)는 사람이 눈으로 가르는 편의이고,
 *    기계가 가르는 자는 이 키다(계약 `RegisterSampleBody.metadata` — 자유 객체).
 *    값은 관리자 대시보드와 **같은 낱말**을 쓴다 — 두 창구가 만든 행이 한 잣대로 걸러지게.
 */
export const QUICK_ORIGIN_KEY = 'origin'
export const QUICK_ORIGIN = 'admin_test_batch'

/**
 * 시료 종류. 🔴 **「테스트 검체」를 유지한다**(2026-09-28 팀장 결정) — 검사 정보를 싣게 된 뒤에도 원장에서
 *    시험 행을 가르는 눈이 이 낱말이다. 「환자 혈액 시료」로 바꾸면 시험 행이 실제 환자 시료와 섞여 읽힌다.
 * ⚠ `sample_type` 은 자유 문자열이다(계약·DDL 둘 다 enum 이 없다).
 */
export const QUICK_TYPE_TEST = '테스트 검체'
/** Reference 는 «시험 검체가 아니다» — 평형추와 짝을 이루는 실제 역할이다(`01 §①`) */
export const QUICK_TYPE_REFERENCE = 'Reference'

/**
 * 회차 표지 — `TEST-YYYYMMDD-HHMMSS`. 사람이 목록에서 한 회차를 묶어 보는 자.
 * 🔴 **초까지 싣는다**(2026-09-28 실제 사고). 분 단위였을 때 같은 분에 다시 누르면 시료 번호(`…-01`~)가 겹쳐
 *    서버(append-only)가 「이미 등록된 시료 번호」로 거절했다. 같은 초에 겹치는 드문 경우는 지금처럼 사유를 보이고
 *    멈춘다 — 🔴 그때 이미 있는 시료를 재사용하지 마라(다른 회차의 시료다).
 */
export function quickTag(now: Date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return (
    `TEST-${String(now.getFullYear())}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`
  )
}

/** `useRegisterSample` 이 받는 변수 꼴 그대로 */
export interface QuickSampleVars {
  sampleId: string
  sampleType: string
  metadata: Record<string, unknown>
  /* ↓ 검사 정보 — 환자 시료에만 싣는다(대조에는 없다) */
  patientId?: string
  rbcConcentration?: number
  bloodDrawDate?: string
  cancerType?: string
  cancerStage?: string
}

/**
 * **환자 시료의 검사 정보 — 시료마다 다르게**(eric 2026-09-28 「시료를 클릭하면 해당 시료의 정보가 나와야 하는 거
 * 아니야? 혈액 환자.. 무슨 암..」). 비어 있으면 모니터링 서랍 · 시료 상세에 볼 것이 없다.
 * 🔴 **개발용 버튼(`VITE_DEV_QUICK_BATCH`) 한정이다.** 「새 작업」 · 시료 등록 화면에는 어떤 기본값도 넣지 않는다.
 * 🔴 **시험 값임이 값 자체에서 보인다** — 환자 번호는 회차 표지(`TEST-…`)로 시작한다. 실제 환자 번호처럼
 *    생긴 값을 짓지 않는다. 암종 · 병기 · 농도는 네 시료가 서로 달라 화면에서 구분된다.
 * ⚠ 모양은 서버가 받는 그대로다(`RegisterSampleBody` — 암종 · 병기는 자유 문자열 · 농도는 숫자 ·
 *   채혈 날짜는 `YYYY-MM-DD` · `app/views/samples.py` 가 `parse_date` 로 읽는다).
 * ⚠ 줄 수가 `QUICK_SAMPLE_COUNT` 보다 적으면 남는 시료는 검사 정보 없이 선다(값을 돌려 쓰지 않는다).
 */
export const QUICK_PATIENT_PROFILES: readonly {
  rbcConcentration: number
  cancerType: string
  cancerStage: string
}[] = [
  { rbcConcentration: 4.2, cancerType: '폐암', cancerStage: 'IIIA' },
  { rbcConcentration: 4.6, cancerType: '유방암', cancerStage: 'IIB' },
  { rbcConcentration: 5.0, cancerType: '대장암', cancerStage: 'IV' },
  { rbcConcentration: 3.8, cancerType: '위암', cancerStage: 'I' },
]

/**
 * **대조(Reference) 시료의 검사 정보** — 🔴 **eric 2026-09-28 결정으로 채운다**(종전 「대조에는 환자 정보를 싣지
 * 않는다」를 뒤집었다). 원문: 「이거 시료 정보가 없는데.. 없으면.. 테스트 작업 돌릴 때.. 강제로 데이터 넣고...
 * 저기에 시료 정보가 보이게 해줘..」.
 * 🔴 **개발용 버튼 한정이다** — 실제 대조 검체의 등록 화면에 이 값을 넣지 않는다.
 * ⚠ 환자 넷과 **구분되는 시험 값**이다: 환자 번호는 `<회차 표지>-REF`, 암종 칸은 「대조군」, 병기는 「해당 없음」.
 */
export const QUICK_REFERENCE_PROFILE: {
  rbcConcentration: number
  cancerType: string
  cancerStage: string
} = { rbcConcentration: 4.5, cancerType: '대조군', cancerStage: '해당 없음' }

/** 회차 표지(`TEST-YYYYMMDD-HHMMSS`)의 날짜 → `YYYY-MM-DD`. 표지 꼴이 아니면 `undefined`(날짜를 짓지 않는다) */
function drawDateOf(tag: string): string | undefined {
  const match = /^TEST-(\d{4})(\d{2})(\d{2})-/u.exec(tag)
  return match === null ? undefined : `${match[1] ?? ''}-${match[2] ?? ''}-${match[3] ?? ''}`
}

/**
 * 이 회차가 만들 시료 본문 — 환자 `QUICK_SAMPLE_COUNT` 명 × 챔버 `QUICK_CHAMBERS_PER_PATIENT` 건 뒤에 Reference
 * `QUICK_REFERENCE_COUNT` 건. 시료 번호는 `<회차 표지>-01` … 차례로 잇는다(환자 1의 두 챔버가 `-01`·`-02`).
 * 🔴 **전부 여섯 칸을 싣는다**(eric 2026-09-28). 한 환자의 시료들은 **같은 환자 번호**(`<회차 표지>-P01` …)와 같은
 *    검사 정보(`QUICK_PATIENT_PROFILES`)를 싣는다 — 서버가 이 번호로 챔버를 환자별로 센다. 대조는
 *    `QUICK_REFERENCE_PROFILE` 와 `<회차 표지>-REF`(대조가 둘 이상이면 `-REF2` …). 채혈 날짜는 회차 날짜다.
 */
export function quickSampleVars(tag: string): QuickSampleVars[] {
  const out: QuickSampleVars[] = []
  const drawDate = drawDateOf(tag)
  const push = (
    reference: boolean,
    patientId: string,
    profile: (typeof QUICK_PATIENT_PROFILES)[number] | undefined,
  ) => {
    out.push({
      sampleId: `${tag}-${String(out.length + 1).padStart(2, '0')}`,
      sampleType: reference ? QUICK_TYPE_REFERENCE : QUICK_TYPE_TEST,
      metadata: { [QUICK_ORIGIN_KEY]: QUICK_ORIGIN, tag },
      ...(profile === undefined
        ? {}
        : {
            patientId,
            rbcConcentration: profile.rbcConcentration,
            cancerType: profile.cancerType,
            cancerStage: profile.cancerStage,
            ...(drawDate === undefined ? {} : { bloodDrawDate: drawDate }),
          }),
    })
  }
  for (let patient = 1; patient <= QUICK_SAMPLE_COUNT; patient += 1) {
    const patientId = `${tag}-P${String(patient).padStart(2, '0')}`
    for (let chamber = 1; chamber <= QUICK_CHAMBERS_PER_PATIENT; chamber += 1)
      push(false, patientId, QUICK_PATIENT_PROFILES[patient - 1])
  }
  for (let refIndex = 1; refIndex <= QUICK_REFERENCE_COUNT; refIndex += 1)
    push(true, `${tag}-REF${refIndex > 1 ? String(refIndex) : ''}`, QUICK_REFERENCE_PROFILE)
  return out
}

/**
 * 어디까지 왔나. 🔴 **진행은 글자로만 말한다** — 막대·백분율을 만들지 않는다
 * (여기서 세는 것은 내가 보낸 요청 수이지 장비의 진행이 아니다).
 */
export type QuickPhase =
  /** 아무것도 안 한 상태 */
  | 'idle'
  /** 자동 준비 중 — 작업 종착 · 격리 해제 · 반출 확인 · 정리 · 초기화 · IDLE 대기(`devQuickReset.ts`) */
  | 'reset'
  /** 소모품(팁 · TC) 보충 중 — 아래 「소모품 자동 보충」 */
  | 'supply'
  /** 시료 등록 중 */
  | 'samples'
  /** 작업 생성 중 */
  | 'batch'
  /** 사전 점검 · 경고 확약 대화(= `useStartGate` 가 쥐고 있다) */
  | 'start'
  /** 시작 명령이 나갔다(202) */
  | 'started'
  /** 🔴 사람이 확약 대화를 닫았다 — **작업은 이미 서버에 있다.** 「없던 일」이 아니다 */
  | 'canceled'
  /** 어느 단계에서 멈췄다 */
  | 'failed'

export interface QuickProgress {
  phase: QuickPhase
  /** 등록에 성공한 시료 수. 실패해도 **그대로 둔다**(서버가 append-only 다) */
  registered: number
  /** 서버가 준 작업 id. 만들기 전에는 `undefined` */
  batchId?: string | undefined
  /** 실패 문구(`error_code` 사전). 🔴 자동 재시도는 없다 */
  error?: string | undefined
  /**
   * 어느 단계에서 멈췄나 — `supply` 면 **시료를 하나도 안 만들었다**(보충이 시료보다 앞이다).
   * `assign` 도 같다 — 웰 배정은 시료 등록보다 앞에서 짓는다.
   * 없으면 시료 등록 이후 단계다(종전 문구 「N건 등록 후 멈췄다」).
   * `stall` 은 시작은 받아졌는데 스텝이 안 나갔거나 곧 멈춘 것이다(`watchStartStall`) — `error` 가 이미 완성된 한 줄이다.
   */
  failedAt?: 'reset' | 'supply' | 'assign' | 'preflight' | 'stall' | undefined
  /** `failedAt: 'reset'` 일 때 자동 준비의 어느 단계에서 멈췄나 */
  resetStep?: QuickResetStep | undefined
}

export const QUICK_IDLE: QuickProgress = { phase: 'idle', registered: 0 }

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * 작업 이름 — eric 2026-09-29 「테스트 작업 돌릴 때는.. 테스트 작업 이라고 이름 붙여줘.. 다른 거 쓰지마..」
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * 이 버튼이 만드는 작업의 이름 — **정확히 이 글자**. 날짜 · 회차 · 번호를 붙이지 않는다(「다른 거 쓰지마」).
 *
 * 🔴 **서버는 작업 이름을 받지 않는다**(`CreateBatchBody` 에 `name` 이 없다 · 요청 93). 서버가 만들 때
 *    `Batch.name = 「<레시피 이름> · <수행인> · <시각>」` 으로 조립해 저장한다(`services/batches.compose_name`).
 *    그래서 작업 생성 본문에 싣는 이 이름은 지금은 버려지고, 화면의 이름 자리는 `batchDisplayName` 이 세운다.
 * ⚠ **레시피 이름을 「테스트 작업」으로 바꾸는 길은 막혀 있다**(2026-09-29 실측) — 설정 레시피의 저장된 본문이
 *   지금 서버 검증을 통과하지 못해 `POST /recipes`(본뜨기) · `PUT /recipes/{id}`(개명 = 새 버전) 둘 다
 *   422 `RECIPE_VALIDATION_FAIL`(`binds` · `verify_grip` · `verify_released` · `collaborative_6axis` 추가 속성)이다.
 *   본문을 고쳐 싣으면 장비 동작이 바뀌므로 하지 않는다.
 * ⚠ 표시용 사전 키 `dev.quickBatchName` 과 같은 글자다(시험이 둘을 대조한다).
 */
export const QUICK_BATCH_NAME = '테스트 작업'

/**
 * 지금 한 회차가 돌고 있는가 — 같은 버튼을 두 번 누르지 못하게 하는 축.
 * 🔴 확약 대화가 떠 있는 `start` 도 **돌고 있는 것**이다. 여기서 다시 누르면 시료 한 벌과
 *    작업이 하나 더 생기고, 앞 회차의 대화는 남의 작업을 가리킨 채로 뜬다.
 */
const RUNNING: readonly QuickPhase[] = ['reset', 'supply', 'samples', 'batch', 'start']

export function quickBusy(progress: QuickProgress): boolean {
  return RUNNING.includes(progress.phase)
}

/**
 * 화면에 설 **한 줄** — 🔴 **멈췄을 때(`failed`)와 사람이 대화를 닫았을 때(`canceled`)만** 있다.
 * 도는 중 · 시작 성공에는 `undefined` 다(eric 2026-09-28 「이거 하지 말고. 그냥 내부적으로 돌려서 시작하라고」) —
 * 진행과 결과는 상시 작업 줄(작업이 실행 중으로 바뀐다)이 보인다. 자동 확약한 경고도 여기 적지 않는다(원장에는 남는다).
 */
export function quickProgressText(progress: QuickProgress): string | undefined {
  const { phase, registered, batchId, error } = progress
  if (phase === 'canceled') return t('dev.quickStep.canceled', { batch: shortId(batchId) })
  if (phase !== 'failed') return undefined
  /*
   * 🔴 실패는 **두 가지를 같이** 적는다 — 무엇이 왔나(에러 코드 사전) + 어디까지 갔나.
   *    「N건 만들고 멈췄다」를 빼면 사람이 남은 시료를 모른 채 다시 누른다(서버는 지우지 않는다).
   */
  // 🔴 정체 한 줄은 감시가 지은 문장 그대로 — 작업은 서버에 `running` 으로 남아 있다(끊지 않았다)
  if (progress.failedAt === 'stall') return error ?? t('error.unknown')
  if (progress.failedAt === 'preflight')
    return t('dev.quickStep.blocked', {
      reason: error ?? t('error.unknown'),
      batch: shortId(batchId),
    })
  if (progress.failedAt === 'reset')
    return t('dev.quickStep.resetFailed', {
      step:
        progress.resetStep === undefined
          ? t('dev.quickStep.reset')
          : t(`dev.quickReset.step.${progress.resetStep}`),
      reason: error ?? t('error.unknown'),
    })
  if (progress.failedAt === 'supply')
    return t('dev.quickStep.supplyFailed', { reason: error ?? t('error.unknown') })
  if (progress.failedAt === 'assign')
    return t('dev.quickStep.assignFailed', { reason: error ?? t('error.unknown') })
  return t('dev.quickStep.failed', {
    reason: error ?? t('error.unknown'),
    done: String(registered),
  })
}

function shortId(value: string | undefined): string {
  return value === undefined ? '' : value.slice(0, 8)
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * 웰 배정 — 2026-09-28 팀장 확인: 버튼으로 만든 작업의 `sample_assignments` 가 빈 배열이었다
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/** 시료 역할 — 레시피 `SampleGroup.role` 과 같은 어휘 */
type QuickRole = NonNullable<SampleGroup['role']>

function roleOfVars(vars: QuickSampleVars): QuickRole {
  return vars.sampleType === QUICK_TYPE_REFERENCE ? 'reference' : 'patient'
}

export type QuickAssignmentsResult =
  | { kind: 'ok'; assignments: BatchAssignment[] }
  /** 그 역할 묶음이 시료 수보다 적다 — 🔴 보내지 않는다(부분 배정은 서버 422) */
  | { kind: 'short'; role: QuickRole; have: number; need: number }

/**
 * **이 레시피의 트레이 자리표** — 규칙은 `recipes/trayPath.ts` `trayPathOf` 한 곳이다(모니터링 · 새 작업과 같은 함수).
 * v0.9.3 서버는 작업 생성 때 배정을 새 형식 자리표로만 잰다 — 옛 형식 레시피로 보내면 모든 웰이 「자리표 밖」으로
 * 422 였다(eric 「입력이 올바르지 않습니다 — 시료 5건 등록 후 멈췄습니다」 · 2026-10-07).
 */
export function quickTrayPath(payload: unknown): TrayPath | undefined {
  return trayPathOf(payload)
}

/**
 * 이 회차 시료를 **레시피 묶음**(`trayPathOf(payload).sample_groups`)에 앉힌다 — `POST /batches` 의 `assignments`.
 *
 * 🔴 **웰 이름을 코드에 박지 않는다.** 한 환자(같은 환자 번호)의 시료들이 `role: patient` 묶음 하나를, 대조가
 *    `role: reference` 묶음 하나를 **레시피가 적은 순서대로** 차지하고, 그 묶음의 웰을 **적힌 차례로 하나씩** 받는다
 *    (환자 1의 두 시료 → P1 묶음의 `A1`·`B1`). 서버는 그 웰로 묶음을 찾아 `sample_ref`(P1 … · REF)를 파생한다
 *    (`CreateBatchBody.assignments` 설명).
 *    ⚠ `role` 이 없으면 계약 기본값 `patient` 로 읽는다(`recipe.schema.json`).
 * 🔴 **전건이 아니면 안 보낸다.** 묶음이 모자라면 `short` — 반쯤 배정된 작업은 서버가 422(`partial_assignment`)로
 *    떨군다. 웰이 `tray_path.order` 밖이거나 빈 묶음도 앉히지 않는다(서버 `assignment_problem` ⓐ).
 * 🔴 **이 규칙은 개발용 버튼 한정이다.** 「새 작업」 화면은 사람이 웰을 고른다(`newBatchAssignments.ts` —
 *    「추론으로 자동 배정하지 않는다」). 여기는 시험 회차가 레시피 모양(환자 4 × 챔버 2 + 대조 1)에 맞춰 지어졌기에 허락된다.
 *    본문의 전건 · 중복 판정은 그 파일의 `assignmentsBody()` 를 그대로 거친다.
 */
export function quickAssignments(
  trayPath: TrayPath | undefined,
  samples: readonly QuickSampleVars[],
): QuickAssignmentsResult {
  const order = new Set(trayPath?.order ?? [])
  /* 묶음 — 역할별 · 레시피 차례 · 자리표(`order`) 안의 웰만 */
  const pools: Record<QuickRole, string[][]> = { patient: [], reference: [] }
  for (const group of trayPath?.sample_groups ?? []) {
    const wells = group.wells.filter((well) => order.has(well))
    if (wells.length > 0) pools[group.role ?? 'patient'].push(wells)
  }
  /* 시료 단위 — 같은 역할 · 같은 환자 번호가 한 묶음을 쓴다(번호가 없으면 시료 하나가 한 단위) */
  const units: { role: QuickRole; ids: string[] }[] = []
  const unitOf = new Map<string, { role: QuickRole; ids: string[] }>()
  for (const vars of samples) {
    const role = roleOfVars(vars)
    const key = `${role}:${vars.patientId ?? vars.sampleId}`
    let unit = unitOf.get(key)
    if (unit === undefined) {
      unit = { role, ids: [] }
      unitOf.set(key, unit)
      units.push(unit)
    }
    unit.ids.push(vars.sampleId)
  }
  const used: Record<QuickRole, number> = { patient: 0, reference: 0 }
  const picks: Record<string, string> = {}
  for (const unit of units) {
    const wells = pools[unit.role][used[unit.role]]
    if (wells === undefined) {
      const need = units.filter((one) => one.role === unit.role).length
      return { kind: 'short', role: unit.role, have: pools[unit.role].length, need }
    }
    if (wells.length < unit.ids.length)
      return { kind: 'short', role: unit.role, have: wells.length, need: unit.ids.length }
    unit.ids.forEach((id, index) => {
      picks[id] = wells[index] ?? ''
    })
    used[unit.role] += 1
  }
  const decision = assignmentsBody(
    samples.map((vars) => vars.sampleId),
    picks,
  )
  // 묶음마다 서로 다른 웰이라 중복 · 부분이 나올 수 없다 — 그래도 한 판정기를 거친다
  return decision.kind === 'full'
    ? { kind: 'ok', assignments: [...decision.assignments] }
    : { kind: 'short', role: 'patient', have: 0, need: samples.length }
}

export function quickAssignShortText(result: {
  role: QuickRole
  have: number
  need: number
}): string {
  return t('dev.quickAssign.short', {
    role: t(
      result.role === 'reference'
        ? 'dev.quickAssign.role.reference'
        : 'dev.quickAssign.role.patient',
    ),
    have: String(result.have),
    need: String(result.need),
  })
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * 소모품 자동 보충 — eric 2026-09-28 「테스트 작업 돌릴 때는 그냥 저거 다 채워줘」
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * 🔴 **이 자동 보충은 «개발용 테스트 작업» 에만 있다. 일반 「새 작업」 흐름에 옮기지 마라.**
 *    보충 창구는 「사람이 소모품을 실제로 채웠다」는 **기록**이다(append-only 원장 `consumable_replenishment`
 *    · 모듈 카운터 리셋). 실장비에서 사람이 채우지 않은 것을 프로그램이 채웠다고 적으면 **거짓 기록**이고,
 *    그 거짓 위에서 사전 점검이 「팁 충분」을 낸다. 시뮬레이터 장부를 채우는 개발 편의라서 여기서만 허락된다
 *    — 이 버튼 자체가 `VITE_DEV_QUICK_BATCH=true` 일 때만 존재한다(`DevQuickBatch.tsx` 머리말).
 *
 * ## 무엇을 · 몇 개로
 * - **대상 모듈** — `GET /modules` 의 `module_type` 이 맞고 **그 소모품 칸(`tips_remaining` · `tc_remaining`)
 *   키가 «있는»** 모듈. 키가 없으면 그 소모품을 안 가진 모듈이다(계약 `ModuleStatus.consumables` · 서버
 *   `services/preflight._consumable_blockers` 가 같은 규칙으로 고른다). 모듈 id 를 박지 않는다.
 * - **만재 수** — 이 회차가 쓸 **레시피의 `payload.labware[]`** 에서 그 종류 랩웨어(`tip_rack` · `tc_stock`)의
 *   칸 수 합. 소모품 요약(`screens/monitoring/supplySummary.ts`)이 칸 수를 읽는 곳과 같은 출처다.
 *   칸은 `well_names` 로 센다 — 서버 `services/preflight.tip_capacity` 의 규칙(「`well_names` 만 simple · full
 *   양쪽에 공통」)을 따른다. 사전 점검이 같은 수로 판정하므로 그 수까지 채우면 그 축은 풀린다.
 *   ⚠ 새 형식(v2) 레시피엔 `payload.labware[]` 가 없다 — 그때는 `declaredSlotsV2`(서버 `recipe_v2.labware.context_of`
 *   규칙)로 센다. 둘을 가르는 곳은 `recipeSlots` 한 곳이다.
 * - 🔴 **`count` 를 생략해 서버에 맡기지 않는다** — module-core 가 생략을 조건 없이 422 로 거절한다(만재값
 *   상수가 모듈 프로파일에 없다 · 2026-09-28 실측). 그래서 레시피 선언을 읽어 싣는다.
 * - 🔴 **칸 수를 못 세면 채우지 않고 멈춘다** — 번들 정의(96)로 메우지 않는다. 지어낸 수로 카운터를 덮으면 장부가 거짓이 된다.
 *   ⚠ **예외 하나 — TC 적재소**(eric 2026-10-19 「소모품 보충에서 멈췄다고 나오는데」): v0.9.4 서버 표준 레시피가 TC 랙을 싣지 않고
 *   서버(위치 등록부 `assembler.tc_stock` · 모듈 능력)에도 칸 수가 없다. 우리 전용 랩웨어라 칸 수가 그림 · 소모품 판과 같은
 *   `TC_STOCK_SLOTS`(3 × 3) 하나뿐이다 — 판이 「0/9」로 보이는 그 9 로 채운다(`fallbackSlots`). 레시피가 선언하면 그것이 먼저다.
 *   한 회차가 TC 9개를 다 쓰므로 이 예외가 없으면 「테스트 작업 시작」이 두 번째마다 멈췄다(10-18 회차 뒤 `tc_remaining` 0).
 * - **이미 가득이면 건너뛴다** — 보충 원장이 append-only 라 매번 한 줄씩 쌓인다.
 *   ⚠ 가득의 판정은 **합계(`tips_remaining`)** 다. 칸별 기록(`tips_by_rack[].used_slots`)은 보충 뒤에도
 *   지워지지 않는 모듈 쪽 어긋남이 있어(2026-09-28 실측 — 합계 192 인데 랙 a 96칸 전부 used) 그것으로 재면
 *   가득인데도 매번 보충한다. 사전 점검도 합계로 판정한다.
 */
export const QUICK_SUPPLY_NOTE = '개발용 테스트 작업 자동 보충'

export type QuickSupplyKind = 'tip' | 'tc'

interface QuickSupplyTarget {
  kind: QuickSupplyKind
  moduleType: ModuleSnapshot['module_type']
  /** `ModuleSnapshot.consumables` 의 잔량 칸 */
  column: string
  /** 그 소모품을 담는 레시피 랩웨어 `type` */
  labwareType: string
  /** 레시피가 그 랩웨어를 선언하지 않을 때의 칸 수 — TC 적재소만(위 머리말 「예외 하나」) */
  fallbackSlots?: number
}

/** 🔴 칸 이름 · 랩웨어 종류는 계약 어휘다. 모듈 id · 수는 여기 없다 — 실물에서 읽는다 */
export const QUICK_SUPPLY_TARGETS: readonly QuickSupplyTarget[] = [
  { kind: 'tip', moduleType: 'staining', column: 'tips_remaining', labwareType: 'tip_rack' },
  {
    kind: 'tc',
    moduleType: 'assembler',
    column: 'tc_remaining',
    labwareType: 'tc_stock',
    fallbackSlots: TC_STOCK_SLOTS,
  },
]

/**
 * 레시피가 선언한 그 종류 랩웨어의 칸 수 합. 그 종류가 없거나 한 장이라도 `well_names` 가 없으면
 * `undefined` — 「모른다」이지 0 이 아니다(서버 `tip_capacity` 와 같은 규칙).
 */
export function declaredSlots(
  labware: readonly RecipeLabwareDef[] | undefined,
  labwareType: string,
): number | undefined {
  const racks = (labware ?? []).filter((entry) => entry.type === labwareType)
  if (racks.length === 0) return undefined
  let sum = 0
  for (const rack of racks) {
    if (!Array.isArray(rack.well_names)) return undefined
    sum += rack.well_names.length
  }
  return sum
}

/**
 * **새 형식(Opentrons protocol/8) 레시피**가 선언한 그 종류 랩웨어의 칸 수 합 — 읽는 규칙은 `recipes/recipeV2View.ts`
 * `labwareOf`(서버 `recipe_v2.labware.context_of` 와 같다) 한 곳이고 세는 규칙은 옛 형식과 같은 `declaredSlots` 다.
 * 그 종류가 없거나 한 장이라도 칸을 못 세면 `undefined` — 「모른다」다.
 */
export function declaredSlotsV2(payload: unknown, labwareType: string): number | undefined {
  return declaredSlots(labwareOf(payload), labwareType)
}

/** 레시피 payload 의 그 종류 칸 수 — 옛 형식(`labware[]`)이든 새 형식이든 `labwareOf` 가 가른다 */
export function recipeSlots(payload: unknown, labwareType: string): number | undefined {
  return declaredSlots(labwareOf(payload), labwareType)
}

export interface QuickSupplyStep {
  moduleId: string
  kind: QuickSupplyKind
  /** 보충 후 잔량(= 레시피가 선언한 칸 수) */
  count: number
  /** 보충 전 모듈이 보고한 잔량. `null` = 모듈이 모른다고 답했다 */
  before: number | null
}

export interface QuickSupplyPlan {
  steps: QuickSupplyStep[]
  /** 채워야 하는데 칸 수를 못 센 것 — 하나라도 있으면 보충하지 않고 멈춘다 */
  uncounted: { moduleId: string; kind: QuickSupplyKind }[]
}

export function quickSupplyPlan(
  modules: readonly ModuleSnapshot[],
  /** 레시피 `payload` 통째 — 옛 형식 · 새 형식 둘 다(`recipeSlots`) */
  payload: unknown,
): QuickSupplyPlan {
  const steps: QuickSupplyStep[] = []
  const uncounted: QuickSupplyPlan['uncounted'] = []
  for (const target of QUICK_SUPPLY_TARGETS) {
    const capacity = recipeSlots(payload, target.labwareType) ?? target.fallbackSlots
    for (const module of modules) {
      if (module.module_type !== target.moduleType) continue
      const bag = module.consumables
      // 🔴 `in` 으로 본다 — 키 부재(그 소모품이 없다)와 `null`(있는데 모른다)이 갈려야 한다
      if (bag === null || bag === undefined || !(target.column in bag)) continue
      const raw = bag[target.column]
      const before = typeof raw === 'number' ? raw : null
      if (capacity === undefined) {
        /*
         * 레시피가 그 랩웨어를 선언하지 않았다(v0.9.4 서버 표준은 어셈블러 TC 랙을 레시피에 싣지 않는다 · 2026-10-18 실측).
         * 🔴 모듈이 잔량을 갖고 있으면 건너뛴다 — 채울 수를 모르는데 멈추면 그 소모품이 충분해도 시작이 막혔다. 충분한지는
         *    서버 사전 점검이 판정한다. 비었는데(0 · 모름) 수를 모를 때만 멈춘다(지어낸 수로 채운 기록을 남기지 않는다)
         */
        if (before === null || before <= 0)
          uncounted.push({ moduleId: module.module_id, kind: target.kind })
        continue
      }
      if (before !== null && before >= capacity) continue // 이미 가득 — 원장에 한 줄 더 남기지 않는다
      steps.push({ moduleId: module.module_id, kind: target.kind, count: capacity, before })
    }
  }
  return { steps, uncounted }
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * 시약 자동 보충 — eric 2026-10-08 「어셈블러에서 염색기 넘어가는 과정에서 일시정지 되었는데 이거 수정해줘」
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * 시약 한 번 보충 — 같은 랩웨어 · 같은 양의 웰을 묶은 것(서버 본문 한 건)
 */
export interface QuickReagentStep {
  moduleId: string
  labwareId: string
  wellNames: string[]
  /** 보충 후 웰마다 양(µL) = 레시피 `loadLiquid.volumeByWell` */
  volume: number
}

/**
 * **레시피가 시약을 둔 웰 중 모듈 장부가 그 양보다 적은(또는 모르는) 웰만** 레시피 양으로 채운다.
 *
 * 🔴 왜 — 팁 · TC 만 채우고 시약은 안 채웠다. 회차가 쌓이자 염색기 워싱용액 웰이 0 이 되어 모듈이 «시약 소진» 보류
 *    (`reagent_exhausted`)로 섰고, 시작 전 점검의 `REAGENT_VOLUME_LOW` 경고는 이 흐름이 확약하고 넘겼다. 보류 중인 염색기는
 *    슬라이드 이송 전 걸쇠 확인을 건너뛰어 6축 L 이송(어셈블러 → 염색기)이 `STAIN_LATCH_FAIL` → 보상 → 일시정지로 멎었다
 *    (dev2 2026-10-08 16:39 · 16:58 · 17:14 세 번).
 * 🔴 양은 **레시피 `loadLiquid` 가 정한 값**이다(새 형식 `commands[]`). 짓지 않는다 — 그 명령이 없는 레시피(옛 형식)면 빈 계획이다.
 * 대상 모듈은 장부(`consumables.reagents_by_well`)에 그 랩웨어를 가진 모듈이다. 장부에 아직 없으면 시약 장부를 가진 모듈이 하나일 때만
 * 그 모듈 — 랩웨어 이름으로 모듈을 짐작하지 않는다.
 */
export function quickReagentPlan(
  modules: readonly ModuleSnapshot[],
  payload: unknown,
): QuickReagentStep[] {
  const wanted = new Map<string, Map<string, number>>()
  const commands =
    typeof payload === 'object' && payload !== null
      ? (payload as { commands?: unknown }).commands
      : undefined
  if (!Array.isArray(commands)) return []
  for (const command of commands as unknown[]) {
    if (typeof command !== 'object' || command === null) continue
    const { commandType, params } = command as { commandType?: unknown; params?: unknown }
    if (commandType !== 'loadLiquid' || typeof params !== 'object' || params === null) continue
    const { labwareId, volumeByWell } = params as { labwareId?: unknown; volumeByWell?: unknown }
    if (typeof labwareId !== 'string' || typeof volumeByWell !== 'object' || volumeByWell === null)
      continue
    const wells = wanted.get(labwareId) ?? new Map<string, number>()
    for (const [well, volume] of Object.entries(volumeByWell as Record<string, unknown>))
      if (typeof volume === 'number' && volume > 0) wells.set(well, volume)
    if (wells.size > 0) wanted.set(labwareId, wells)
  }
  /* 장부(`consumables.reagents_by_well`)를 가진 모듈마다 웰 → 지금 양 */
  const ledgers = new Map<string, Map<string, number | null>>()
  for (const module of modules) {
    const ledger = (module.consumables as { reagents_by_well?: unknown } | null | undefined)
      ?.reagents_by_well
    if (!Array.isArray(ledger)) continue
    const have = new Map<string, number | null>()
    for (const row of ledger as unknown[]) {
      if (typeof row !== 'object' || row === null) continue
      const { labwareId, wellName, volume } = row as Record<string, unknown>
      if (typeof labwareId === 'string' && typeof wellName === 'string')
        have.set(`${labwareId}|${wellName}`, typeof volume === 'number' ? volume : null)
    }
    ledgers.set(module.module_id, have)
  }
  /*
   * 대상 모듈 — 장부에 그 랩웨어를 가진 모듈. 🔴 **장부에 아직 그 랩웨어가 없으면** 시약 장부를 가진 모듈이 «하나»일 때만 그 모듈이다
   * (2026-10-15 로컬 — 모듈 장부는 `ambient.1` 인데 레시피는 `ambient-1` 이라 한 칸도 안 채워 `REAGENT_SHORTAGE_AT_START` 로 멎었다.
   * 모듈은 레시피 이름으로 보낸 보충을 받아 장부에 새 키로 싣는다). 둘 이상이면 고르지 않는다 — 랩웨어 이름으로 짐작하지 않는다
   */
  const owners = [...ledgers.keys()]
  const ownerOf = (labwareId: string): string | undefined => {
    for (const [moduleId, have] of ledgers)
      if ([...have.keys()].some((key) => key.split('|')[0] === labwareId)) return moduleId
    return owners.length === 1 ? owners[0] : undefined
  }
  const steps: QuickReagentStep[] = []
  for (const [labwareId, wells] of wanted) {
    const moduleId = ownerOf(labwareId)
    if (moduleId === undefined) continue
    const have = ledgers.get(moduleId) ?? new Map<string, number | null>()
    const byVolume = new Map<number, string[]>()
    for (const [well, volume] of wells) {
      const now = have.get(`${labwareId}|${well}`)
      if (typeof now === 'number' && now >= volume) continue
      byVolume.set(volume, [...(byVolume.get(volume) ?? []), well])
    }
    for (const [volume, wellNames] of byVolume)
      steps.push({ moduleId, labwareId, wellNames: wellNames.sort(), volume })
  }
  return steps
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * 폐기통 교체 자동 기록 — eric 2026-10-07 「테스트 작업 시작이잖아.. 점검이나 이런 거 다 되도록 세팅하고.. 스텝만 밟으면 돼」
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * 🔴 **이 자동 기록도 «개발용 테스트 작업» 에만 있다**(소모품 자동 보충과 같은 규율 · `QUICK_SUPPLY_NOTE` 머리말).
 *    교체 기록은 「사람이 폐기통을 실제로 비웠다」는 append-only 원장(`waste_replacement`)이다 — 실장비에서
 *    프로그램이 비웠다고 적으면 거짓 기록이다. 시뮬레이터 장부를 맞추는 개발 편의라 여기서만 허락된다.
 *
 * 왜 — v0.9.3 시작 전 점검 `PREFLIGHT_WASTE_NOT_EMPTIED`(`fail` · 건너뛰기 설정이 가리지 않는다)는 「그 통을
 *    `trash` 랩웨어로 가진 레시피로 시작한 배치가 있고 그 뒤 교체 기록이 없으면」 막는다(서버
 *    `maintenance.emptying_required`). 새 형식 레시피는 염색기·어셈블러 폐기통을 싣고 있어 **한 회차마다** 두 통이
 *    「비워야 함」이 된다. 판정은 서버 칸 `WasteContainer.emptying_required` 하나다 — 여기서 다시 셈하지 않는다.
 */
export const QUICK_WASTE_NOTE = '개발용 테스트 작업 자동 기록'

/** 교체 기록을 남길 폐기통 — 서버가 「비워야 함」이라 한 통만(`emptying_required === true`) · 레지스트리 차례 그대로 */
export function quickWastePlan(containers: readonly WasteContainer[]): string[] {
  return containers
    .filter((container) => container.emptying_required === true)
    .map((container) => container.container_id)
}

/**
 * 사전 점검이 **「모름」으로 답한 핸드오프 자리**(`HANDOFF_POINT_OCCUPIED_AT_START` · verdict `inconclusive` · 장부에 그 자리 행이 없다).
 * 🔴 왜 — v0.9.4 가 자리 이름을 바꿨다(`<m>.slide_rear` → `<m>.grip_tray_on_slide` 등 · 마이그레이션 0074). 시뮬레이터 장부에는 옛 이름 행만
 *    남아 새 자리가 「모름」이 됐고, 코어가 트레이 적재 건을 못 열어 `PREFLIGHT_TRAY_NOT_LOADED` 로 시작이 막혔다(2026-10-18 실측).
 *    개발용 흐름이 이 자리를 «비었다»로 확약하면(`IFC-RO-54`) 장부에 행이 서고 다음 점검이 지나간다.
 * 🔴 **`fail`(실제로 찼다고 보고된 자리)은 고르지 않는다** — 찬 자리를 비었다고 적으면 실제 트레이 위에 다른 트레이를 놓게 된다.
 */
export function unreadHandoffPoints(
  preflight:
    | {
        blockers?: readonly {
          code: string
          verdict?: string | null
          module_id?: string | null
          item?: string | null
        }[]
      }
    | undefined,
): { moduleId: string; pointId: string }[] {
  const out: { moduleId: string; pointId: string }[] = []
  for (const blocker of preflight?.blockers ?? []) {
    if (blocker.code !== 'HANDOFF_POINT_OCCUPIED_AT_START' || blocker.verdict !== 'inconclusive')
      continue
    if (
      typeof blocker.module_id !== 'string' ||
      typeof blocker.item !== 'string' ||
      blocker.item === ''
    )
      continue
    out.push({ moduleId: blocker.module_id, pointId: blocker.item })
  }
  return out
}

/** 트레이 적재 확약 대기 — 간격 · 횟수(상한 약 30초). 로컬 코어 자동 확약은 2초 뒤 · 5초 주기로 돈다 */
export const QUICK_LOAD_WAIT_MS = 2_000
export const QUICK_LOAD_WAIT_TRIES = 15

/**
 * 점검이 **트레이 적재 확약만** 기다리는가 — 통과 못 했고, `fail` 인 차단이 전부 `PREFLIGHT_TRAY_NOT_LOADED` 다.
 * 🔴 다른 `fail`(자리 점유 · 소모품 …)이 하나라도 있으면 거짓 — 기다려도 안 풀리니 바로 시작 게이트로 넘겨 사유를 보인다.
 */
export function waitingForTrayLoad(
  preflight:
    | { passed?: boolean; blockers?: readonly { code: string; verdict?: string | null }[] }
    | undefined,
): boolean {
  if (preflight === undefined || preflight.passed === true) return false
  const failing = (preflight.blockers ?? []).filter((blocker) => blocker.verdict === 'fail')
  return (
    failing.length > 0 && failing.every((blocker) => blocker.code === 'PREFLIGHT_TRAY_NOT_LOADED')
  )
}
